import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { DatePicker } from "@/modules/shared/ui/components/date-picker"
import { Label } from "@/modules/shared/ui/components/label"
import { startOfToday } from "date-fns"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { WeightedSkillInput } from "./WeightedSkillInput"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import {
  selectCurrentOrganization,
  selectCurrentOrganizationId,
} from "@/modules/organizations/store/organizationSlice"
import { toast } from "sonner"
import { JobType } from "../../../enums/JobType"
import { LocationType } from "../../../enums/LocationType"
import { SeniorityLevel } from "../../../enums/SeniorityLevel"
import { VacancyListItemDto } from "../../../dtos/VacancyListItemDto"
import { CreateVacancyCommand } from "../../../dtos/CreateVacancyDto"
import { VacancyStatus } from "../../../enums/VacancyStatus"
import { useVacancies } from "@/modules/recruitment/hooks/useVacancies"
import { WeightedSkill } from "@/modules/recruitment/entities/WeightedSkill"
import { Department } from "@/modules/recruitment/enums/Department"
import { useLookupService } from "@/modules/shared/infrastructure/lookup-service"
import { InfoTooltip } from "@/modules/shared/ui/components/info-tooltip"
import {
  ShieldAlert,
  Briefcase,
  Building2,
  MapPin,
  Banknote,
  GraduationCap,
  Clock,
  TrendingUp,
  ListChecks,
  Star,
  Sparkles,
  Award,
  AlignLeft,
  Type,
  AlertTriangle,
} from "lucide-react"
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "@/modules/shared/ui/components/alert"
import { useQuery } from "@tanstack/react-query"

interface CreateVacancyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vacancy?: VacancyListItemDto
  isEditMode?: boolean
}

export function CreateVacancyDialog({
  open,
  onOpenChange,
  vacancy,
  isEditMode,
}: CreateVacancyDialogProps) {
  const organizationId = useSelector(selectCurrentOrganizationId)
  const organization = useSelector(selectCurrentOrganization)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [warningMessage, setWarningMessage] = useState<string | null>(null)
  const { updateVacancyList, createNewVacancy } = useVacancies()
  const lookupService = useLookupService()

  // Backend-configured caps live in the `common."Lookups"` table (category
  // "MaxLength", names VACANCY_TITLE / VACANCY_DESCRIPTION). The defaults
  // below match the seeded values so the dialog stays functional on the
  // first paint before the query resolves.
  const { data: vacancyMaxLengths } = useQuery({
    queryKey: ["vacancy-max-lengths"],
    queryFn: () => lookupService.getVacancyMaxLengths(),
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const titleMaxLength = vacancyMaxLengths?.titleMaxLength ?? 200
  const descriptionMaxLength = vacancyMaxLengths?.descriptionMaxLength ?? 1500

  const [educationLevels, setEducationLevels] = useState<
    { id: string; name: string }[]
  >([])

  const getJobTypeLabel = (value: JobType): string => {
    switch (value) {
      case JobType.Permanent:
        return "Permanent"
      case JobType.PartTime:
        return "Part Time"
      case JobType.Temporary:
        return "Temporary"
      case JobType.Contract:
        return "Contract"
      case JobType.Internship:
        return "Internship"
      case JobType.Volunteer:
        return "Volunteer"
      default:
        return ""
    }
  }
  const getSeniorityLevelLabel = (value: SeniorityLevel): string => {
    switch (value) {
      case SeniorityLevel.Junior:
        return "Junior"
      case SeniorityLevel.Mid:
        return "Mid Level"
      case SeniorityLevel.Senior:
        return "Senior"
      case SeniorityLevel.Lead:
        return "Lead"
      case SeniorityLevel.Principal:
        return "Principal"
      case SeniorityLevel.Manager:
        return "Manager"
      case SeniorityLevel.Director:
        return "Director"
      case SeniorityLevel.Executive:
        return "Executive"
      default:
        return ""
    }
  }

  const seniorityLevelOptions: { label: string; value: SeniorityLevel }[] = [
    { label: "Junior", value: SeniorityLevel.Junior },
    { label: "Mid Level", value: SeniorityLevel.Mid },
    { label: "Senior", value: SeniorityLevel.Senior },
    { label: "Lead", value: SeniorityLevel.Lead },
    { label: "Principal", value: SeniorityLevel.Principal },
    { label: "Manager", value: SeniorityLevel.Manager },
    { label: "Director", value: SeniorityLevel.Director },
    { label: "Executive", value: SeniorityLevel.Executive },
  ]

  const getDepartmentLabel = (value: Department): string => {
    switch (value) {
      case Department.Accounting:
        return "Accounting"
      case Department.Admin:
        return "Admin"
      case Department.Advertising:
        return "Advertising"
      case Department.Defence:
        return "Defence"
      case Department.Agriculture:
        return "Agriculture"
      case Department.Aviation:
        return "Aviation"
      case Department.Entertainment:
        return "Entertainment"
      case Department.Banking:
        return "Banking"
      case Department.Construction:
        return "Construction"
      case Department.BusinessManagement:
        return "Business Management"
      case Department.CallCentre:
        return "Call Centre"
      case Department.Petrochemical:
        return "Petrochemical"
      case Department.SocialServices:
        return "Social Services"
      case Department.DesignServices:
        return "Design Services"
      case Department.Warehousing:
        return "Warehousing"
      case Department.Education:
        return "Education"
      case Department.Engineering:
        return "Engineering"
      case Department.FMCG:
        return "FMCG"
      case Department.GovernmentAndParastatals:
        return "Government And Parastatals"
      case Department.Sports:
        return "Sports"
      case Department.Hospitality:
        return "Hospitality"
      case Department.HumanResources:
        return "Human Resources"
      case Department.IT:
        return "IT"
      case Department.Insurance:
        return "Insurance"
      case Department.Investment:
        return "Investment"
      case Department.Legal:
        return "Legal"
      case Department.Logistics:
        return "Logistics"
      case Department.Manufacturing:
        return "Manufacturing"
      case Department.Marketing:
        return "Marketing"
      case Department.Medical:
        return "Medical"
      case Department.Mining:
        return "Mining"
      case Department.General:
        return "General"
      case Department.Media:
        return "Media"
      case Department.RealEstate:
        return "Real Estate"
      case Department.Retail:
        return "Retail"
      case Department.Security:
        return "Security"
      case Department.Sales:
        return "Sales"
      case Department.Technology:
        return "Technology"
      case Department.Telecommunications:
        return "Telecommunications"
      case Department.Clothing:
        return "Clothing"
      case Department.TradesAndServices:
        return "Trades And Services"
      case Department.TravelAndTourism:
        return "Travel And Tourism"
      case Department.Finance:
        return "Finance"
      case Department.Automotive:
        return "Automotive"
      case Department.Academic:
        return "Academic"
      case Department.Chemical:
        return "Chemical"
      case Department.Fashion:
        return "Fashion"
      case Department.ImportAndExport:
        return "Import And Export"
      case Department.HealthFitnessAndBeauty:
        return "Health Fitness And Beauty"
      case Department.Management:
        return "Management"
      case Department.Consulting:
        return "Consulting"
      case Department.Motoring:
        return "Motoring"
      case Department.NGOAndNonProfit:
        return "NGO And Non Profit"
      case Department.PRAndCommunication:
        return "PR And Communication"
      case Department.Procurement:
        return "Procurement"
      case Department.Property:
        return "Property"
      case Department.Purchasing:
        return "Purchasing"
      case Department.Research:
        return "Research"
      case Department.Stockbroking:
        return "Stockbroking"
      default:
        return ""
    }
  }

  const departmentOptions: { label: string; value: Department }[] = [
    { label: "Accounting", value: Department.Accounting },
    { label: "Admin", value: Department.Admin },
    { label: "Advertising", value: Department.Advertising },
    { label: "Defence", value: Department.Defence },
    { label: "Agriculture", value: Department.Agriculture },
    { label: "Aviation", value: Department.Aviation },
    { label: "Entertainment", value: Department.Entertainment },
    { label: "Banking", value: Department.Banking },
    { label: "Construction", value: Department.Construction },
    { label: "Business Management", value: Department.BusinessManagement },
    { label: "Call Centre", value: Department.CallCentre },
    { label: "Petrochemical", value: Department.Petrochemical },
    { label: "Social Services", value: Department.SocialServices },
    { label: "Design Services", value: Department.DesignServices },
    { label: "Warehousing", value: Department.Warehousing },
    { label: "Education", value: Department.Education },
    { label: "Engineering", value: Department.Engineering },
    { label: "FMCG", value: Department.FMCG },
    {
      label: "Government And Parastatals",
      value: Department.GovernmentAndParastatals,
    },
    { label: "Sports", value: Department.Sports },
    { label: "Hospitality", value: Department.Hospitality },
    { label: "Human Resources", value: Department.HumanResources },
    { label: "IT", value: Department.IT },
    { label: "Insurance", value: Department.Insurance },
    { label: "Investment", value: Department.Investment },
    { label: "Legal", value: Department.Legal },
    { label: "Logistics", value: Department.Logistics },
    { label: "Manufacturing", value: Department.Manufacturing },
    { label: "Marketing", value: Department.Marketing },
    { label: "Medical", value: Department.Medical },
    { label: "Mining", value: Department.Mining },
    { label: "General", value: Department.General },
    { label: "Media", value: Department.Media },
    { label: "Real Estate", value: Department.RealEstate },
    { label: "Retail", value: Department.Retail },
    { label: "Security", value: Department.Security },
    { label: "Sales", value: Department.Sales },
    { label: "Technology", value: Department.Technology },
    { label: "Telecommunications", value: Department.Telecommunications },
    { label: "Clothing", value: Department.Clothing },
    { label: "Trades And Services", value: Department.TradesAndServices },
    { label: "Travel And Tourism", value: Department.TravelAndTourism },
    { label: "Finance", value: Department.Finance },
    { label: "Automotive", value: Department.Automotive },
    { label: "Academic", value: Department.Academic },
    { label: "Chemical", value: Department.Chemical },
    { label: "Fashion", value: Department.Fashion },
    { label: "Import And Export", value: Department.ImportAndExport },
    {
      label: "Health Fitness And Beauty",
      value: Department.HealthFitnessAndBeauty,
    },
    { label: "Management", value: Department.Management },
    { label: "Consulting", value: Department.Consulting },
    { label: "Motoring", value: Department.Motoring },
    { label: "NGO And Non Profit", value: Department.NGOAndNonProfit },
    { label: "PR And Communication", value: Department.PRAndCommunication },
    { label: "Procurement", value: Department.Procurement },
    { label: "Property", value: Department.Property },
    { label: "Purchasing", value: Department.Purchasing },
    { label: "Research", value: Department.Research },
    { label: "Stockbroking", value: Department.Stockbroking },
  ]

  const getLocationTypeLabel = (value: LocationType): string => {
    switch (value) {
      case LocationType.Remote:
        return "Remote"
      case LocationType.OnSite:
        return "Onsite"
      case LocationType.Hybrid:
        return "Hybrid"
      default:
        return ""
    }
  }

  const jobTypeOptions: { label: string; value: JobType }[] = [
    { label: "Permanent", value: JobType.Permanent },
    { label: "Part Time", value: JobType.PartTime },
    { label: "Temporary", value: JobType.Temporary },
    { label: "Contract", value: JobType.Contract },
    { label: "Internship", value: JobType.Internship },
    { label: "Volunteer", value: JobType.Volunteer },
  ]

  const locationTypeOptions: { label: string; value: LocationType }[] = [
    { label: "Remote", value: LocationType.Remote },
    { label: "On-Site", value: LocationType.OnSite },
    { label: "Hybrid", value: LocationType.Hybrid },
  ]

  const [formData, setFormData] = useState<CreateVacancyCommand>({
    title: "",
    description: "",
    jobType: JobType.Permanent,
    department: Department.General,
    locationType: LocationType.Remote,
    status: VacancyStatus.Active,
    minimumSalary: 0,
    maximumSalary: 0,
    baseScore: 0,
    organizationId: organizationId || "",
    applicationDeadline: new Date(
      Date.now() + 30 * 24 * 60 * 60 * 1000,
    ).toISOString(), // Default 30 days
    keyResponsibilities: [],
    requiredSkills: [],
    optionalSkills: [],
    educationLevel: "",
    yearsOfExperience: 0,
    certifications: [],
    seniorityLevel: SeniorityLevel.Junior,
  })

  const [errors, setErrors] = useState<{
    title?: string
    description?: string
    minimumSalary?: string
    maximumSalary?: string
    keyResponsibilities?: string
    applicationDeadline?: string
  }>({})

  useEffect(() => {
    if (open && organizationId) {
      if (vacancy) {
        setFormData({
          title: vacancy.title,
          description: vacancy.description || "",
          jobType: vacancy.jobType || JobType.Permanent,
          department: vacancy.department || Department.General,
          locationType: vacancy.locationType || LocationType.Remote,
          status:
            (vacancy.status as
              | VacancyStatus.Active
              | VacancyStatus.Closed
              | VacancyStatus.Expired) || VacancyStatus.Active,
          minimumSalary: vacancy.minimumSalary || 0,
          maximumSalary: vacancy.maximumSalary || 0,
          baseScore: vacancy.baseScore || 0,
          organizationId: vacancy.organizationId || organizationId,
          applicationDeadline:
            vacancy.applicationDeadline ||
            new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          keyResponsibilities: vacancy.keyResponsibilities ?? [],
          requiredSkills: vacancy.requiredSkills ?? [],
          optionalSkills: vacancy.optionalSkills ?? [],
          educationLevel: vacancy.educationLevel || "",
          yearsOfExperience: vacancy.yearsOfExperience || 0,
          certifications: vacancy.certifications ?? [],
          seniorityLevel: vacancy.seniorityLevel || SeniorityLevel.Junior,
          sageMetadata: vacancy.sageMetadata ?? null,
        })
      } else {
        setFormData({
          title: "",
          description: "",
          jobType: JobType.Permanent,
          department: Department.General,
          locationType: LocationType.Remote,
          status: VacancyStatus.Active,
          minimumSalary: 0,
          maximumSalary: 0,
          organizationId: organizationId,
          applicationDeadline: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          baseScore: 0,
          keyResponsibilities: [],
          requiredSkills: [],
          optionalSkills: [],
          educationLevel: "",
          yearsOfExperience: 0,
          certifications: [],
          seniorityLevel: SeniorityLevel.Junior,
        })
      }
      setErrors({})
    }
  }, [open, organizationId, vacancy])

  useEffect(() => {
    lookupService
      .getEducationLevels()
      .then((levels) => setEducationLevels(levels))
      .catch((error) =>
        console.error("Failed to load education levels:", error),
      )
  }, [lookupService])

  const validateForm = () => {
    const newErrors: {
      title?: string
      description?: string
      minimumSalary?: string
      maximumSalary?: string
      keyResponsibilities?: string
      applicationDeadline?: string
    } = {}

    if (!formData.title.trim()) {
      newErrors.title = "Title is required"
      toast.error("Title is required")
    } else if (formData.title.length > titleMaxLength) {
      newErrors.title = `Title must be ${titleMaxLength} characters or fewer`
      toast.error(
        `Title is too long (${formData.title.length}/${titleMaxLength})`,
      )
    }
    if (!formData.description.trim()) {
      newErrors.description = "Description is required"
      toast.error("Description is required")
    } else if (formData.description.length > descriptionMaxLength) {
      newErrors.description = `Description must be ${descriptionMaxLength} characters or fewer`
      toast.error(
        `Description is too long (${formData.description.length}/${descriptionMaxLength})`,
      )
    }
    if (formData.minimumSalary <= 0) {
      newErrors.minimumSalary = "Minimum salary cannot be zero or negative"
      toast.error("Minimum salary cannot be zero or negative")
    }
    if (formData.maximumSalary < formData.minimumSalary) {
      newErrors.maximumSalary =
        "Maximum salary must be greater than or equal to minimum salary"
      toast.error(
        "Maximum salary must be greater than or equal to minimum salary",
      )
    }
    if (formData.keyResponsibilities.filter((r) => !r.deleted).length === 0) {
      newErrors.keyResponsibilities =
        "At least one key responsibility is required"
      toast.error("At least one key responsibility is required")
    }
    if (formData.applicationDeadline) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (new Date(formData.applicationDeadline) < today) {
        newErrors.applicationDeadline =
          "Application deadline cannot be in the past"
        toast.error("Application deadline cannot be in the past")
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setWarningMessage(null)
    setIsSubmitting(true)
    try {
      const filterDeleted = (items: WeightedSkill[]) =>
        items.filter((item) => !item.deleted)

      const createVacancyCommand: CreateVacancyCommand = {
        ...formData,
        organizationId: formData.organizationId || organizationId || "",
        keyResponsibilities: filterDeleted(formData.keyResponsibilities),
        requiredSkills: filterDeleted(formData.requiredSkills),
        optionalSkills: filterDeleted(formData.optionalSkills),
        certifications: filterDeleted(formData.certifications),
      }

      if (isEditMode && vacancy) {
        await updateVacancyList(vacancy.id, createVacancyCommand)
        toast.success("Vacancy updated successfully")
      } else {
        await createNewVacancy(createVacancyCommand)
        toast.success("Vacancy created successfully")
      }
      onOpenChange(false)
    } catch (error: unknown) {
      const message =
        typeof error === "string"
          ? error
          : "Something went wrong. Please try again."
      setWarningMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[min(90vw,48rem)] max-h-[90vh] overflow-y-auto overflow-x-hidden bg-card">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {vacancy ? "Edit Vacancy" : "Add a new vacancy"}
          </DialogTitle>
          <DialogDescription>
            {vacancy
              ? "Update the details of the vacancy"
              : "Enter Job details below to add them to your company directory"}
          </DialogDescription>
        </DialogHeader>
        {formData.sageMetadata && (
          <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-blue-500" />
            <span>
              Sage 300 vacancy — Position{" "}
              <span className="font-mono font-semibold">
                {formData.sageMetadata.positionCode}
              </span>{" "}
              ({formData.sageMetadata.companyCode} /{" "}
              {formData.sageMetadata.companyRuleCode})
            </span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="title">Title</Label>
                <InfoTooltip
                  icon={Type}
                  title="Job Title"
                  description="The job title displayed to candidates on the listing. Make it clear and specific to attract the right applicants."
                />
              </div>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value })
                  if (errors.title) setErrors({ ...errors, title: undefined })
                }}
                placeholder="e.g. Senior Software Engineer"
                maxLength={titleMaxLength}
                className={errors.title ? "border-destructive" : ""}
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="description">Description</Label>
              <InfoTooltip
                icon={AlignLeft}
                title="Job Description"
                description="A brief overview of the role shown on the public listing. Summarise the purpose of the position and what makes it an attractive opportunity."
              />
            </div>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value })
                if (errors.description)
                  setErrors({ ...errors, description: undefined })
              }}
              placeholder="Brief description of the vacancy..."
              rows={3}
              maxLength={descriptionMaxLength}
              className={errors.description ? "border-destructive" : ""}
            />
            <div className="flex items-center justify-between text-xs">
              {errors.description ? (
                <p className="text-destructive">{errors.description}</p>
              ) : (
                <span />
              )}
              <span
                className={
                  formData.description.length >= descriptionMaxLength
                    ? "text-destructive font-medium"
                    : formData.description.length > descriptionMaxLength * 0.9
                      ? "text-amber-600"
                      : "text-muted-foreground"
                }
              >
                {formData.description.length} / {descriptionMaxLength}
              </span>
            </div>
          </div>

          <div
            className={`-mx-6 grid w-[calc(100%+3rem)] gap-3 px-6
                            ${vacancy ? "grid-cols-3" : "grid-cols-2"}
                        `}
          >
            {/* Job Type */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="jobType">Job Type</Label>
                <InfoTooltip
                  icon={Briefcase}
                  title="Employment Type"
                  description="The nature of the employment arrangement - permanent, contract, part-time, and so on. Shown to candidates on the listing."
                />
              </div>
              <Select
                value={formData.jobType}
                onValueChange={(value: JobType) =>
                  setFormData({ ...formData, jobType: value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue>{getJobTypeLabel(formData.jobType)}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {jobTypeOptions.map((jobType) => (
                    <SelectItem key={jobType.value} value={jobType.value}>
                      {jobType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Department */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="department">Department</Label>
                <InfoTooltip
                  icon={Building2}
                  title="Department"
                  description="The business unit or industry category this vacancy belongs to. Used to categorise and filter vacancies."
                />
              </div>
              <Select
                value={formData.department}
                onValueChange={(value: Department) =>
                  setFormData({ ...formData, department: value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue>
                    {getDepartmentLabel(formData.department)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {departmentOptions.map((department) => (
                    <SelectItem key={department.value} value={department.value}>
                      {department.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location Type */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="locationType">Location Type</Label>
                <InfoTooltip
                  icon={MapPin}
                  title="Work Location"
                  description="Where the work will be performed - remote, on-site at a company location, or a hybrid of both."
                />
              </div>
              <Select
                value={formData.locationType}
                onValueChange={(value: LocationType) =>
                  setFormData({ ...formData, locationType: value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue>
                    {getLocationTypeLabel(formData.locationType)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {locationTypeOptions.map((locationType) => (
                    <SelectItem
                      key={locationType.value}
                      value={locationType.value}
                    >
                      {locationType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="minSalary">Minimum Annual Salary</Label>
                <InfoTooltip
                  icon={Banknote}
                  iconClassName="text-green-400"
                  title={`Annual Salary (${organization?.currency})`}
                  description={
                    <>
                      The lower bound of the annual salary range in{" "}
                      {organization?.currency}. Displayed to candidates as
                      part of the compensation package.
                    </>
                  }
                />
              </div>
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                  ZAR
                </span>
                <Input
                  id="minSalary"
                  type="number"
                  value={
                    formData.minimumSalary === 0 ? "" : formData.minimumSalary
                  }
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      minimumSalary:
                        e.target.value === "" ? 0 : Number(e.target.value),
                    })
                    if (errors.minimumSalary)
                      setErrors({ ...errors, minimumSalary: undefined })
                    if (errors.maximumSalary)
                      setErrors({ ...errors, maximumSalary: undefined }) // Clear max salary error too as it depends on min
                  }}
                  className={`pr-12 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${errors.minimumSalary ? "border-destructive" : ""}`}
                  placeholder="0"
                />
              </div>
              {errors.minimumSalary && (
                <p className="text-sm text-destructive">
                  {errors.minimumSalary}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="maxSalary">Maximum Annual Salary</Label>
                <InfoTooltip
                  icon={Banknote}
                  iconClassName="text-green-400"
                  title="Annual Salary (ZAR)"
                  description="The upper bound of the annual salary range in ZAR. Must be greater than or equal to the minimum salary."
                />
              </div>
              <div className="relative">
                <Input
                  id="maxSalary"
                  type="number"
                  value={
                    formData.maximumSalary === 0 ? "" : formData.maximumSalary
                  }
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      maximumSalary:
                        e.target.value === "" ? 0 : Number(e.target.value),
                    })
                    if (errors.maximumSalary)
                      setErrors({ ...errors, maximumSalary: undefined })
                  }}
                  className={`pr-12 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${errors.maximumSalary ? "border-destructive" : ""}`}
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                  ZAR
                </span>
              </div>
              {errors.maximumSalary && (
                <p className="text-sm text-destructive">
                  {errors.maximumSalary}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="applicationDeadline">Application Deadline</Label>
            <DatePicker
              id="applicationDeadline"
              placeholder="Select a deadline"
              disabled={{ before: startOfToday() }}
              value={
                formData.applicationDeadline
                  ? new Date(formData.applicationDeadline)
                  : undefined
              }
              onChange={(date) => {
                setFormData({
                  ...formData,
                  applicationDeadline: date ? date.toISOString() : undefined,
                })
                if (errors.applicationDeadline)
                  setErrors({ ...errors, applicationDeadline: undefined })
              }}
            />
            {errors.applicationDeadline && (
              <p className="text-sm text-destructive">
                {errors.applicationDeadline}
              </p>
            )}
          </div>

          {/* Job Description Details */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground">
              Job Requirements
            </h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="educationLevel">Education Level</Label>
                  <InfoTooltip
                    icon={GraduationCap}
                    iconClassName="text-purple-400"
                    title="Education Level"
                    description="The minimum academic qualification required for this role. Used to filter and score candidates accordingly."
                  />
                </div>
                <Select
                  value={formData.educationLevel}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, educationLevel: value })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select education level">
                      {formData.educationLevel || "Select education level"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {educationLevels?.map((level) => (
                      <SelectItem key={level.id} value={level.name}>
                        {level.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="yearsOfExperience">Years of Experience</Label>
                  <InfoTooltip
                    icon={Clock}
                    title="Experience Required"
                    description="The minimum number of years of relevant experience expected for this role."
                  />
                </div>
                <Input
                  id="yearsOfExperience"
                  type="number"
                  value={
                    formData.yearsOfExperience === 0
                      ? ""
                      : formData.yearsOfExperience
                  }
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      yearsOfExperience:
                        e.target.value === "" ? 0 : Number(e.target.value),
                    })
                  }
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="baseScore">Base Score</Label>
                <InfoTooltip
                  icon={ShieldAlert}
                  iconClassName="text-amber-400"
                  title="Auto-Rejection Threshold"
                  description={
                    <>
                      Candidates who score below this threshold are
                      automatically rejected and notified. Set to{" "}
                      <span className="text-white font-medium">0</span> to
                      disable auto-rejection.
                    </>
                  }
                />
              </div>
              <Input
                id="baseScore"
                type="number"
                value={formData.baseScore === 0 ? "" : formData.baseScore}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    baseScore:
                      e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="seniorityLevel">Seniority Level</Label>
                <InfoTooltip
                  icon={TrendingUp}
                  title="Seniority Level"
                  description="The experience tier expected for this role - from Junior through to Executive. Helps candidates self-assess their fit."
                />
              </div>
              <Select
                value={formData.seniorityLevel}
                onValueChange={(value: SeniorityLevel) =>
                  setFormData({ ...formData, seniorityLevel: value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue>
                    {getSeniorityLevelLabel(formData.seniorityLevel)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {seniorityLevelOptions.map((seniorityLevel) => (
                    <SelectItem
                      key={seniorityLevel.value}
                      value={seniorityLevel.value}
                    >
                      {seniorityLevel.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="keyResponsibilities">
                  Key Responsibilities{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <InfoTooltip
                  icon={ListChecks}
                  maxWidth={280}
                  title="Key Responsibilities"
                  description={
                    <>
                      The core tasks this role will perform. Each
                      responsibility contributes to the candidate's match
                      score - assign a weight of{" "}
                      <span className="text-white font-medium">1-5</span> to
                      control how much each one influences the score. A
                      weight of 5 has the greatest impact.
                    </>
                  }
                />
              </div>
              <WeightedSkillInput
                id="keyResponsibilities"
                values={formData.keyResponsibilities}
                onChange={(values: WeightedSkill[]) => {
                  setFormData({ ...formData, keyResponsibilities: values })
                  if (errors.keyResponsibilities)
                    setErrors({ ...errors, keyResponsibilities: undefined })
                }}
                placeholder="Add a responsibility and press Enter"
                weightPlaceholder="Weight"
              />
              {errors.keyResponsibilities && (
                <p className="text-sm text-destructive">
                  {errors.keyResponsibilities}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="requiredSkills">Required Skills</Label>
                <InfoTooltip
                  icon={Star}
                  iconClassName="text-amber-400"
                  maxWidth={280}
                  title="Required Skills"
                  description={
                    <>
                      Must-have skills for this role. These are factored into
                      the candidate's match score - use a weight of{" "}
                      <span className="text-white font-medium">1-5</span> to
                      indicate how critical each skill is. Higher weights
                      increase the score impact for candidates who have them.
                    </>
                  }
                />
              </div>
              <WeightedSkillInput
                id="requiredSkills"
                values={formData.requiredSkills}
                onChange={(values: WeightedSkill[]) => {
                  setFormData({ ...formData, requiredSkills: values })
                }}
                placeholder="Add a skill and press Enter"
                weightPlaceholder="Weight"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="optionalSkills">Optional Skills</Label>
                <InfoTooltip
                  icon={Sparkles}
                  maxWidth={280}
                  title="Optional Skills"
                  description={
                    <>
                      Nice-to-have skills that improve a candidate's match
                      score without being mandatory. Assign a weight of{" "}
                      <span className="text-white font-medium">1-5</span> -
                      candidates with these skills will rank higher than
                      those without.
                    </>
                  }
                />
              </div>
              <WeightedSkillInput
                id="optionalSkills"
                values={formData.optionalSkills}
                onChange={(values: WeightedSkill[]) =>
                  setFormData({ ...formData, optionalSkills: values })
                }
                placeholder="Add a skill and press Enter"
                weightPlaceholder="Weight"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="certifications">Certifications</Label>
                <InfoTooltip
                  icon={Award}
                  iconClassName="text-amber-400"
                  maxWidth={280}
                  title="Certifications"
                  description={
                    <>
                      Professional certifications or credentials relevant to
                      the role. Weighted{" "}
                      <span className="text-white font-medium">1-5</span>.
                      Candidates who hold higher-weighted certifications will
                      receive a larger boost to their overall match score.
                    </>
                  }
                />
              </div>
              <WeightedSkillInput
                id="certifications"
                values={formData.certifications}
                onChange={(values: WeightedSkill[]) =>
                  setFormData({ ...formData, certifications: values })
                }
                placeholder="Add a certification and press Enter"
                weightPlaceholder="Weight"
              />
            </div>
          </div>

          {warningMessage && (
            <Alert className="border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertTitle className="text-amber-800 dark:text-amber-300">
                {isEditMode
                  ? "Couldn't update vacancy"
                  : "Couldn't create vacancy"}
              </AlertTitle>
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                {warningMessage}
              </AlertDescription>
            </Alert>
          )}

          <div className="-mx-6 grid w-[calc(100%+3rem)] grid-cols-2 gap-3 px-6 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false)
                setWarningMessage(null)
              }}
              className="w-full"
            >
              Cancel
            </Button>

            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update Vacancy"
                  : "Create Vacancy"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
